import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import ProteinSearchInput from './AaSequenceSearchInput';
import {ProteinSpecifier} from './util/AaSequenceAlignmentConstants';
import specifierForProtein from './util/specifierForAaSequence';

interface Props {
  proteinSpecifiers: Array<ProteinSpecifier>;
  templateProteinIndex?: number | null;
  onAddProteinSpecifier: (proteinSpecifier: ProteinSpecifier) => void;
  onRemoveProteinAt: (rowIndex: number) => void;
  onSetTemplateProteinAt: (rowIndex: number | null) => void;
  allowSetTemplate: boolean;
}

class ChooseProteinAlignmentPane extends React.Component<Props> {
  static defaultProps = {
    allowSetTemplate: true,
  };

  renderProteinRow = (proteinName: string, rowIndex: number): JSX.Element => {
    const allowDelete = this.props.allowSetTemplate || rowIndex !== this.props.templateProteinIndex;
    return (
      <tr className="template _test-proteinRow" key={`${proteinName}-${rowIndex}`}>
        {this.renderProteinCell(proteinName, rowIndex === this.props.templateProteinIndex)}
        {this.props.allowSetTemplate ? this.renderTemplateCell(rowIndex) : <td />}
        {allowDelete ? this.renderDeleteCell(rowIndex) : <td />}
      </tr>
    );
  };

  renderProteinCell = (proteinName: string, isTemplateProtein: boolean): JSX.Element => (
    <td {...getTestProps({component: 'ChooseProteinAlignmentPane', element: 'proteinCell'})}>
      <span className="chooseProteinAlignment-proteinName">{proteinName}</span>
      <br />
      {isTemplateProtein && (
        <span
          className="chooseProteinAlignment-proteinDescription"
          {...getTestProps({
            component: 'ChooseProteinAlignmentPane',
            element: 'proteinDescriptionTemplate',
          })}
        >
          Template
        </span>
      )}
    </td>
  );

  renderTemplateCell = (rowIndex: number): JSX.Element => {
    if (rowIndex === this.props.templateProteinIndex) {
      return (
        <td
          className="chooseProteinAlignment-templateCell"
          {...getTestProps({component: 'ChooseProteinAlignmentPane', element: 'templateCell'})}
        >
          <a
            className="chooseProteinAlignment-hoverButton"
            onClick={() => {
              this.props.onSetTemplateProteinAt(null);
            }}
          >
            Unset Template
          </a>
        </td>
      );
    } else {
      return (
        <td
          className="chooseProteinAlignment-templateCell"
          {...getTestProps({component: 'ChooseProteinAlignmentPane', element: 'templateCell'})}
        >
          <a
            className="chooseProteinAlignment-hoverButton"
            onClick={() => {
              this.props.onSetTemplateProteinAt(rowIndex);
            }}
          >
            Set Template
          </a>
        </td>
      );
    }
  };

  renderDeleteCell = (rowIndex: number): JSX.Element => (
    <td className="chooseProteinAlignment-deleteCell">
      <a
        className="faded u-danger-pointer chooseProteinAlignment-hoverButton"
        onClick={() => {
          this.props.onRemoveProteinAt(rowIndex);
        }}
      >
        <BenchlingIcon icon="delete" />
      </a>
    </td>
  );

  renderProteinTable = (): JSX.Element => {
    if (this.props.proteinSpecifiers.length === 0) {
      return (
        <div className="chooseProteinAlignment-noProteinsText">No AA sequences chosen for alignment.</div>
      );
    } else {
      return (
        <table className="table table-hover">
          <tbody>
            {this.props.proteinSpecifiers.map((proteinSpecifier, rowIndex) =>
              this.renderProteinRow(proteinSpecifier.name, rowIndex)
            )}
          </tbody>
        </table>
      );
    }
  };

  override render(): JSX.Element {
    return (
      <div>
        <div className="u-tiny-spacing-bottom">Add AA sequences to your alignment:</div>
        <ProteinSearchInput
          onChooseProtein={(protein) => {
            this.props.onAddProteinSpecifier(specifierForProtein(protein));
          }}
        />
        <h4 className="chooseProteinAlignment-tableTitle">AA Sequences</h4>
        {this.renderProteinTable()}
      </div>
    );
  }
}

export default ChooseProteinAlignmentPane;
